import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { movies, tvShows, type MediaItem } from "@/mocks/media";
import MovieCard from "@/components/base/MovieCard";
import SectionHeading from "@/components/base/SectionHeading";
import Toast from "@/components/base/Toast";
import { useFavorites } from "@/hooks/useFavorites";
import { typeLabel } from "@/utils/format";
import DetailHero from "./components/DetailHero";
import EpisodeList from "./components/EpisodeList";

export default function Detail() {
  const { id } = useParams();
  const allMedia: MediaItem[] = [...movies, ...tvShows];
  const item = allMedia.find((m) => m.id === id);

  const { toggleFavorite, isFavorite } = useFavorites();
  const [toast, setToast] = useState("");
  const toastTimer = useRef<number | null>(null);

  useEffect(() => {
    if (item) {
      document.title = `${item.title} - 详细介绍 - 黄色影院`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) {
        meta.setAttribute(
          "content",
          `${item.title}（${item.year}）${typeLabel(item.type)}，类型：${item.genres.join("、")}，地区：${item.region}。${item.description}`,
        );
      }
    }
  }, [item]);

  const showToast = (message: string) => {
    setToast(message);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(""), 2200);
  };

  if (!item) {
    return (
      <div className="w-full px-4 md:px-8 py-20 min-h-[60vh] flex flex-col items-center">
        <span className="w-20 h-20 flex items-center justify-center rounded-2xl bg-background-100 border border-background-300 mb-6">
          <i className="ri-file-unknow-line text-4xl text-primary-400"></i>
        </span>
        <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
          影片不存在
        </h1>
        <p className="text-sm text-foreground-600 mb-8 text-center max-w-md">
          没有找到对应的影视内容，可能链接已失效。你可以返回首页，或搜索其他影片。
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3 mb-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-400 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
          <Link
            to="/search"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-secondary-400 text-foreground-800 text-sm font-medium hover:bg-secondary-500/20 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-search-line"></i>
            搜索影片
          </Link>
        </div>
        <div className="w-full max-w-4xl">
          <h2 className="text-lg font-semibold text-foreground-900 mb-5 text-center">热门搜索建议</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
            {[...allMedia]
              .sort((a, b) => b.views - a.views)
              .slice(0, 4)
              .map((s) => (
                <MovieCard key={s.id} item={s} />
              ))}
          </div>
        </div>
      </div>
    );
  }

  const related = allMedia
    .filter(
      (m) =>
        m.id !== item.id &&
        (m.type === item.type || m.region === item.region) &&
        m.genres.some((g) => item.genres.includes(g)),
    )
    .slice(0, 4);

  const fallbackRelated = allMedia.filter((m) => m.id !== item.id).slice(0, 4);
  const recommendations = related.length >= 4 ? related : fallbackRelated;

  const handleShare = async () => {
    const shareData = {
      title: `${item.title} - 黄色影院`,
      text: `看看《${item.title}》的详细介绍`,
      url: window.location.href,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        showToast("分享成功（演示功能）");
      } else {
        await navigator.clipboard.writeText(window.location.href);
        showToast("链接已复制（演示功能）");
      }
    } catch {
      showToast("分享已取消（演示功能）");
    }
  };

  const handleDemo = (action: string) => {
    showToast(`${action}功能为演示状态，暂不支持实际播放`);
  };

  const fav = isFavorite(item.id);

  return (
    <div className="w-full">
      <div className="w-full px-4 md:px-8 pt-6">
        <nav className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
          <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">
            主页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link
            to={item.type === "movie" ? "/category/movie" : "/category/tv"}
            className="hover:text-primary-400 transition-colors cursor-pointer"
          >
            {typeLabel(item.type)}
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800 truncate">{item.title}</span>
        </nav>
      </div>

      <DetailHero
        item={item}
        isFavorite={fav}
        onToggleFavorite={() => {
          toggleFavorite(item.id);
          showToast(fav ? "已取消收藏" : "已加入收藏");
        }}
        onShare={handleShare}
        onDemo={handleDemo}
      />

      <div className="w-full px-4 md:px-8 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
          <div>
            <div className="rounded-lg border border-background-300 bg-background-100 p-5 md:p-6 mb-8">
              <h2 className="text-lg font-semibold text-foreground-950 mb-5">影片信息</h2>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                <div className="flex gap-3">
                  <dt className="text-foreground-500 w-16 shrink-0">类型</dt>
                  <dd className="text-foreground-900">{item.genres.join(" / ")}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="text-foreground-500 w-16 shrink-0">地区</dt>
                  <dd className="text-foreground-900">{item.region}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="text-foreground-500 w-16 shrink-0">年份</dt>
                  <dd className="text-foreground-900">{item.year} 年</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="text-foreground-500 w-16 shrink-0">
                    {item.type === "movie" ? "片长" : "集数"}
                  </dt>
                  <dd className="text-foreground-900">
                    {item.type === "movie" ? item.duration : `共 ${item.episodes} 集 · ${item.duration}`}
                  </dd>
                </div>
                <div className="flex gap-3">
                  <dt className="text-foreground-500 w-16 shrink-0">导演</dt>
                  <dd className="text-foreground-900">{item.director}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="text-foreground-500 w-16 shrink-0">主演</dt>
                  <dd className="text-foreground-900">{item.actors.join("、")}</dd>
                </div>
              </dl>
            </div>

            <div className="mb-8">
              <h2 className="text-lg font-semibold text-foreground-950 mb-4">剧情简介</h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                {item.description}
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mt-3">
                《{item.title}》由 {item.director} 执导，{item.actors.join("、")} 等主演，是一部
                {item.year} 年的{item.region}{typeLabel(item.type)}作品。影片围绕
                {item.genres.join("、")}展开，剧情跌宕、画面质感出众，适合喜爱该题材的观众细细品味。
              </p>
            </div>

            {item.type === "tv" && (
              <div className="mb-8">
                <h2 className="text-lg font-semibold text-foreground-950 mb-2">
                  剧集列表
                  <span className="ml-2 text-xs font-normal text-foreground-500">
                    共 {item.episodes} 集 · 演示内容
                  </span>
                </h2>
                <p className="text-sm text-foreground-500 mb-4">
                  以下剧集为前端静态演示数据，点击不提供真实播放。
                </p>
                <EpisodeList count={item.episodes} onDemo={handleDemo} />
              </div>
            )}
          </div>

          <aside>
            <SectionHeading title="所属分类" icon="ri-folders-line" />
            <div className="flex flex-wrap gap-2 mb-8">
              <Link
                to={item.type === "movie" ? "/category/movie" : "/category/tv"}
                className="px-3 py-1.5 rounded-md bg-primary-100 text-primary-700 text-sm border border-primary-200 hover:bg-primary-200 transition-colors cursor-pointer"
              >
                {typeLabel(item.type)}频道
              </Link>
              {item.genres.map((g) => (
                <Link
                  key={g}
                  to={item.type === "movie" ? "/category/movie" : "/category/tv"}
                  className="px-3 py-1.5 rounded-md bg-secondary-100 text-secondary-900 text-sm border border-secondary-200 hover:bg-secondary-200 transition-colors cursor-pointer"
                >
                  {g}
                </Link>
              ))}
            </div>

            <SectionHeading title="相关推荐" icon="ri-movie-line" />
            <div className="space-y-3">
              {recommendations.map((r) => (
                <Link
                  key={r.id}
                  to={`/detail/${r.id}`}
                  className="group flex gap-3 rounded-lg border border-background-300 bg-background-100 p-2.5 hover:border-primary-400 transition-colors cursor-pointer"
                >
                  <div className="w-14 h-20 rounded-md overflow-hidden bg-background-200 shrink-0">
                    <img
                      src={r.poster}
                      alt={`${r.title} 海报`}
                      title={r.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0 flex-1 py-1">
                    <h4 className="text-sm font-medium text-foreground-950 truncate group-hover:text-primary-400 transition-colors">
                      {r.title}
                    </h4>
                    <p className="text-xs text-foreground-500 mt-1">
                      {r.year} · {r.region}
                    </p>
                    <p className="text-xs text-foreground-500 mt-1 truncate">
                      {r.genres.join(" / ")}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </aside>
        </div>
      </div>

      <Toast message={toast} visible={toast !== ""} />
    </div>
  );
}